// src/components/OrdersPage/OrderFormModal.jsx (или где он у вас лежит)

import React, { useEffect, useState } from "react";
import { authFetch } from "../api/api";
import Card from "../UI/Card";
import { useTranslation } from "react-i18next";

const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;

const emptyForm = {
  client: null,
  ordertype: "unofficial",
  destination_city: null,
  expeditor: null,
  commission_rate: null,
  production_deposit_percent: 30,
  expeditor_warehouse: null,

  recipient_name: "",
  recipient_phone: "",
  recipient_address: "",
  recipient_zip: "",

  start_date: null,
  expeditor_tracking: "",

  total_volume: null,
  total_gross_weight: null,

  price_delivery: null,
  price_to_expeditor: null,

  status: "pending",
  
  comment: "", // <-- Добавлено поле для комментария

  items: [],
};

/* функция рассчета цены в зависимости от кол-ва */
const getPriceByQty = (product, qty) => {
  if (!product?.price_tiers) return product?.standard_price;

  const tier = product.price_tiers.find(t =>
    qty >= t.min_qty && (t.max_qty === null || qty <= t.max_qty)
  );

  return tier ? Number(tier.price) : Number(product.standard_price || 0);
};

/* ================= UI ================= */
const Row = ({ label, children }) => (
  <div className="grid grid-cols-3 gap-3 items-center">
    <div className="text-sm font-semibold">{label}</div>
    <div className="col-span-2">{children}</div>
  </div>
);

const Autocomplete = ({ label, value, onChange, options, getLabel }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { t } = useTranslation("OrdersPage");

  const selectedLabel = options.find(o => String(o.id) === String(value));

  const filtered = options.filter((o) =>
    getLabel(o).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Row label={label}>
      <div className="relative">
        <input
          className="border p-2 w-full"
          value={
            open
              ? search
              : selectedLabel
                ? getLabel(selectedLabel)
                : ""
          }
          placeholder={t("type_to_search")}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
            onChange(null); // сбрасываем выбор при вводе
          }}
        />

        {open && (
          <div className="absolute z-50 bg-white border w-full max-h-40 overflow-auto">
            {filtered.length === 0 && (
              <div className="p-2 text-gray-400">No results</div>
            )}

            {filtered.map((opt) => (
              <div
                key={opt.id}
                className="p-2 hover:bg-gray-200 cursor-pointer"
                onClick={() => {
                  onChange(opt.id);
                  setSearch("");
                  setOpen(false);
                }}
              >
                {getLabel(opt)}
              </div>
            ))}
          </div>
        )}
      </div>
    </Row>
  );
};

const OrderFormModal = ({ order, onClose, onSave }) => {
  const [form, setForm] = useState(emptyForm);
  const [products, setProducts] = useState([]);
  const [cities, setCities] = useState([]);
  const [clients, setClients] = useState([]);
  const [expeditors, setExpeditors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const isEdit = Boolean(order?.id);
  const { t } = useTranslation("OrdersPage");

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    const load = async () => {
      const res = await authFetch(`${BASE_URL}/api/products/`);
      setProducts(await res.json());
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      const res = await authFetch(`${BASE_URL}/api/cities/`);
      setCities(await res.json());
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      const res = await authFetch(`${BASE_URL}/api/clients/`);
      setClients(await res.json());
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      const res = await authFetch(`${BASE_URL}/api/expeditors/`);
      setExpeditors(await res.json());
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      const res = await authFetch(`${BASE_URL}/api/warehouses/`);
      setWarehouses(await res.json());
    };
    load();
  }, []);

  /* ================= LOAD ORDER ================= */
  useEffect(() => {
    if (!order) {
      setForm(emptyForm);
      return;
    }

    setForm({
      ...emptyForm,
      ...order,
      items:
        order.items?.map((i) => ({
          ...i,
          is_client_price_overridden:
            i.is_client_price_overridden ?? false,
          is_purchase_price_overridden:
            i.is_purchase_price_overridden ?? false,
        })) || [],
    });
  }, [order]);

  /* ================= ITEMS ================= */
  const addItem = () => {
    setForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          product: "",
          quantity: 1,
          client_price: "",
          purchase_price: "",
          is_client_price_overridden: false,
          is_purchase_price_overridden: false,
        },
      ],
    }));
  };

  const updateItem = (index, key, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      const item = { ...items[index], [key]: value };

      const product = products.find(
        (p) => Number(p.id) === Number(item.product)
      );

      if (product) {
        const qty = Number(item.quantity || 1);

        if (!item.is_client_price_overridden) {
          item.client_price = getPriceByQty(product, qty);
        }

        if (!item.is_purchase_price_overridden) {
          item.purchase_price = product.purchase_price ?? "";
        }
      }

      if (key === "quantity") {
        const product = products.find(
          (p) => Number(p.id) === Number(item.product)
        );
        if (product && !item.is_client_price_overridden) {
          item.client_price = getPriceByQty(product, Number(value));
        }
      }

      if (key === "client_price") {
        item.is_client_price_overridden = true;
      }

      if (key === "purchase_price") {
        item.is_purchase_price_overridden = true;
      }

      items[index] = item;
      return { ...prev, items };
    });
  };

  const removeItem = (index) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  /* ================= PROFIT ================= */
  const calcProfit = (item) => {
    const q = Number(item.quantity || 0);
    const c = Number(item.client_price || 0);
    const p = Number(item.purchase_price || 0);
    return ((c - p) * q).toFixed(2);
  };

  /* ================= SAVE ================= */
  const save = async () => {
    const url = isEdit
      ? `${BASE_URL}/api/orders/${order.id}/`
      : `${BASE_URL}/api/orders/`;

    const method = isEdit ? "PATCH" : "POST";

    const payload = {
      ...form,

      client: form.client ? Number(form.client) : null,
      destination_city: form.destination_city ? Number(form.destination_city) : null,
      expeditor: form.expeditor ? Number(form.expeditor) : null,
      expeditor_warehouse: form.expeditor_warehouse ? Number(form.expeditor_warehouse) : null,

      items: form.items.map((i) => ({
        product: i.product ? Number(i.product) : null,
        quantity: Number(i.quantity || 1),
        client_price: i.client_price === "" ? null : Number(i.client_price),
        purchase_price: i.purchase_price === "" ? null : Number(i.purchase_price),
        is_client_price_overridden: i.is_client_price_overridden,
        is_purchase_price_overridden: i.is_purchase_price_overridden,
      })),
    };
    
    const res = await authFetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      console.log(await res.json());
      alert("Ошибка сохранения");
      return;
    }

    onSave?.();
    onClose?.();
  };

  /* ================= UI ================= */
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-[900px] max-w-[95vw] max-h-[90vh] overflow-y-auto p-6 space-y-5">
        <h2 className="text-xl font-bold">
          {isEdit ? "Edit Order" : t("add_order")}
        </h2>

        <div>
          <Autocomplete
            label={t("client")}
            value={form.client}
            options={clients}
            onChange={(id) => setForm({ ...form, client: id })}
            getLabel={(c) => c.name}
          />

          <Row label={t("status")}>
            <select
              className="border p-2 w-full"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option value="pending">{t("pending")}</option>
              <option value="completed">{t("completed")}</option>
            </select>
          </Row>

          <Row label="Type">
            <select
              className="border p-2 w-full"
              value={form.ordertype}
              onChange={(e) => setForm({ ...form, ordertype: e.target.value })}
            >
              <option value="official">official</option>
              <option value="unofficial">unofficial</option>
            </select>
          </Row>

          <Row label={`${t("commission_rate")} (%)`}>
            <input
              type="number"
              className="border p-2 w-full"
              value={form.commission_rate || ""}
              onChange={(e) => setForm({ ...form, commission_rate: e.target.value })}
            />
          </Row>

          <Row label="Per cent for starting production">
            <input
              type="number"
              className="border p-2 w-full"
              value={form.production_deposit_percent ?? ""}
              onChange={(e) => setForm({ ...form, production_deposit_percent: e.target.value })}
              min="0"
              max="100"
              step="1"
            />
          </Row>

          <Row label={t("start_date")}>
            <input
              type="date"
              className="border p-2 w-full"
              value={form.start_date || ""}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            />
          </Row>

          <Autocomplete
            label={t("destination_city")}
            value={form.destination_city}
            options={cities}
            onChange={(id) => setForm({ ...form, destination_city: id })}
            getLabel={(c) => c.name}
          />

          <Row label={t("recipient_name")}>
            <input
              className="border p-2 w-full"
              value={form.recipient_name || ""}
              onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
            />
          </Row>

          <Row label={t("recipient_phone")}>
            <input
              className="border p-2 w-full"
              value={form.recipient_phone || ""}
              onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })}
            />
          </Row>

          <Row label={t("recipient_address")}>
            <input
              className="border p-2 w-full"
              value={form.recipient_address || ""}
              onChange={(e) => setForm({ ...form, recipient_address: e.target.value })}
            />
          </Row>

          <Row label={t("recipient_zip")}>
            <input
              className="border p-2 w-full"
              value={form.recipient_zip || ""}
              onChange={(e) => setForm({ ...form, recipient_zip: e.target.value })}
            />
          </Row>

          <Row label={t("expeditor_tracking")}>
            <input
              className="border p-2 w-full"
              value={form.expeditor_tracking || ""}
              onChange={(e) => setForm({ ...form, expeditor_tracking: e.target.value })}
            />
          </Row>

          <Autocomplete
            label={t("expeditor")}
            value={form.expeditor}
            options={expeditors}
            onChange={(id) => setForm({ ...form, expeditor: id })}
            getLabel={(e) => `${e.name} (${e.type})`}
          />

          <Autocomplete
            label={t("warehouse")}
            value={form.expeditor_warehouse}
            options={warehouses}
            onChange={(id) => setForm({ ...form, expeditor_warehouse: id })}
            getLabel={(w) => `${w.name}${w.city_name ? ` (${w.city_name})` : ""}`}
          />

          <Row label={t("total_volume")}>
            <input
              className="border p-2 w-full"
              value={form.total_volume || ""}
              onChange={(e) => setForm({ ...form, total_volume: e.target.value })}
            />
          </Row>

          <Row label={t("gross_weight")}>
            <input
              className="border p-2 w-full"
              value={form.total_gross_weight || ""}
              onChange={(e) => setForm({ ...form, total_gross_weight: e.target.value })}
            />
          </Row>

          <Row label={t("price_delivery")}>
            <input
              className="border p-2 w-full"
              value={form.price_delivery || ""}
              onChange={(e) => setForm({ ...form, price_delivery: e.target.value })}
            />
          </Row>

          <Row label={t("price_to_expeditor")}>
            <input
              className="border p-2 w-full"
              value={form.price_to_expeditor || ""}
              onChange={(e) => setForm({ ...form, price_to_expeditor: e.target.value })}
            />
          </Row>

          {/* ================= COMMENT FIELD ================= */}
          <Row label={t("comment") || "Комментарий"}>
            <textarea
              className="border p-2 w-full min-h-[80px]"
              placeholder="Введите комментарий к заказу..."
              value={form.comment || ""}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
            />
          </Row>
          {/* ================================================= */}

          {form.items.map((item, idx) => (
            <div key={idx} className="border p-3 mt-3 space-y-2">
              <Autocomplete
                label={t("product")}
                value={item.product}
                options={products}
                onChange={(id) => updateItem(idx, "product", id)}
                getLabel={(p) => p.name_en}
              />

              <Row label={t("quantity")}>
                <input
                  type="number"
                  className="border p-2 w-full"
                  value={item.quantity || 1}
                  onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                />
              </Row>

              <Row label={t("client_price")}>
                <input
                  className="border p-2 w-full"
                  value={item.client_price ?? ""}
                  onChange={(e) => updateItem(idx, "client_price", e.target.value)}
                />
              </Row>

              <Row label={t("purchase_price")}>
                <input
                  className="border p-2 w-full"
                  value={item.purchase_price ?? ""}
                  onChange={(e) => updateItem(idx, "purchase_price", e.target.value)}
                />
              </Row>

              <Row label={t("profit")}>
                <div className="font-bold text-green-600">
                  ¥{calcProfit(item)}
                </div>
              </Row>

              <button
                onClick={() => removeItem(idx)}
                className="text-red-600 text-sm"
              >
                {t("delete")}
              </button>
            </div>
          ))}

          <button
            onClick={addItem}
            className="bg-green-500 text-white px-2 py-1 rounded mt-2"
          >
            {t("add_product")}
          </button>
        </div>

        {/* ================= BUTTONS ================= */}
        <div className="flex justify-end gap-2 pt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-400 rounded"
          >
            {t("cancel")}
          </button>

          <button
            onClick={save}
            className="px-4 py-2 bg-blue-600 text-white rounded"
          >
            {t("save")}
          </button>
        </div>
      </Card>
    </div>
  );
};

export default OrderFormModal;